<template>
  <b-container fluid class="user-edit">
    <page-title icon="fas fa-user">
      <translate>Edit User</translate>
    </page-title>
    <b-row class="align-items-center mb-1">
      <b-col class="text-center" style="flex-basis: 150px; flex-grow: 0;">
        <div v-show="user.profile_picture">
          <b-img v-if="user.profile_picture" rounded="rounded" :src="user.profile_picture.replace('=s96-c', '=s2000')" style="max-width: 100px; max-height: 100px; width: auto; height: auto;" width="100" height="100" blank-color="#777" alt="img" class="m-1" /><br>
          <b-button size="sm" v-b-modal.modalUploadImage>
            <translate>Change</translate>
          </b-button>
          <b-button size="sm" variant="danger" @click.stop="deleteProfileImage">
            <font-awesome-icon icon="trash-alt" size="2x" style="font-size: 16px;vertical-align: middle" />
          </b-button>
        </div>
        <div v-show="!user.profile_picture">
          <b-img v-if="!user.profile_picture" rounded="circle" src="https://www.gravatar.com/avatar/default?s=100&r=pg&d=mm" width="100" height="100" blank-color="#777" alt="img" class="m-1" />
          <b-button size="sm" v-b-modal.modalUploadImage v-translate>Add Photo</b-button>
        </div>
      </b-col>
      <b-col>
        <h2>{{user.name}}
          <b-badge variant="warning" v-if="userIsPendingApproval" :title="$gettextInterpolate($gettext('Waiting for %{brandName} approval'), { brandName })" v-translate>Pending approval</b-badge>
          <b-button variant="danger" size="sm" v-show="user.active" @click="disableUser" :disabled="userIsGcmAdmin" v-translate>Disable User</b-button>
          <b-button variant="success" size="sm" v-show="!user.active && !userHasSystemDisableReason" @click="enableUser" v-translate>Enable User</b-button>
        </h2>
        <div class="mobile-apps" v-if="userMobileApps.length > 0">
          <h6 class="mb-0" v-translate>Apps in use</h6>
          <p><span v-for="(app, index) in userMobileApps" :key="index">
              <b-badge>{{app.name}} ({{app.version}})</b-badge>&nbsp;
            </span></p>
        </div>
        <p v-translate="{createdAt: moment(user.created_at).format('L LT')}">User since: %{createdAt}</p>
      </b-col>
    </b-row>
    <b-alert class="mt-3" variant="info" :show="userIsPendingApproval">
      <span v-translate="{brandName}">This courier is pending %{brandName} approval. It cannot be enabled until %{brandName} approves it, but its documentation can still be completed. It will receive its password by e-mail once approved.</span>
    </b-alert>
    <b-alert class="mt-3" variant="warning" :show="setCompanyWarning">
      <h4 v-translate>Warning</h4>
      <h5 v-translate>You need to add your user to a Company before be able to operate.</h5>
    </b-alert>
    <b-card no-body class="user-edit__tabs">
      <b-tabs card v-model="tabIndex">
        <b-tab :title="$gettext('Personal Info')">
          <template #title>
            <translate>Personal Info</translate>
            <b-button size="sm" style="line-height: 1.3; padding: 0 5px;" v-b-modal.modalChangePassword v-if="(isSuperAdmin || isGcmAdmin) && !userIsGcmAdmin">
              <translate>Change Password</translate>
            </b-button>
          </template>
          <b-row>
            <b-col>
              <b-form @submit.prevent="updateUser">
                <b-form-row>
                  <b-form-group class="col-12">
                    <b-form-checkbox size="md" v-model="user.second_factor_enabled" :value="1" :unchecked-value="0" switch>
                      <translate>Enable Two-Factor Authentication (2FA)</translate>
                    </b-form-checkbox>
                  </b-form-group>

                  <b-form-group class="col-md-6 col-lg-4">
                    <label for="user-name" v-translate>Name</label>
                    <b-form-input id="user-name" v-model="user.name" type="text" />
                  </b-form-group>

                  <b-form-group class="col-md-6 col-lg-4">
                    <label for="user-email" v-translate>Email</label>
                    <b-form-input id="user-email" v-model="user.email" type="email" />
                  </b-form-group>

                  <b-form-group class="col-md-6 col-lg-4">
                    <label for="user-tin" v-translate>TIN</label>
                    <b-form-input id="user-tin" v-model="user.tin" type="text" />
                  </b-form-group>

                  <b-form-group class="col-12">
                    <label for="user-phone" v-translate>Phone</label>
                    <b-form-row class="align-items-center">
                      <b-col v-if="user.phone" style="white-space: nowrap; flex-grow: 0;">
                        {{user.phone | phone}}
                      </b-col>
                      <b-col style="white-space: nowrap; flex-grow: 0;">
                        <b-button class="mr-3" size="sm" v-b-modal.modalChangePhone v-translate>Change Phone</b-button>
                      </b-col>
                      <b-col>
                        <b-form-checkbox style="white-space: nowrap;" v-model="user.send_notifications" :value="1" :unchecked-value="0">
                          <translate>Send Notifications</translate>
                        </b-form-checkbox>
                      </b-col>
                    </b-form-row>

                    <b-modal id="modalChangePhone" ref="modalChangePhone" hide-footer @shown="phone = null" @hide="phone = null">
                      <template #modal-title>
                        <translate>Change User's Phone</translate>
                      </template>
                      <template #modal-header-close><img src="@/assets/img/ic_close.svg" alt="×" /></template>
                      <form @submit.stop.prevent="changePhone">
                        <b-form-row>
                          <b-form-group class="col-12">
                            <cleave ref="phoneControl" :placeholder="$gettext('E.g.: +XX XX XXX XXXX')" :options="{ phone: true, phoneRegionCode: this.$cookies.get('country').toLowerCase() }" class="form-control" v-model="phone"></cleave>
                            <b-form-text v-translate>There won't be any validation so check twice what are you changing. International format, please</b-form-text>
                          </b-form-group>
                        </b-form-row>
                        <b-form-row>
                          <b-form-group class="col-12 form-actions">
                            <b-button type="submit" :disabled="!phone" variant="secondary" size="md" v-translate>Change</b-button>
                            <b-button type="button" @click.prevent="$refs.modalChangePhone.hide()" variant="outline-secondary" size="md" v-translate>Cancel</b-button>
                          </b-form-group>
                        </b-form-row>
                      </form>
                    </b-modal>

                  </b-form-group>

                  <b-form-group class="col-12">
                    <b-form-row class="align-items-center" v-show="user.profile_address">
                      <b-col v-if="user.profile_address && user.profile_address.place && user.profile_address.place.formatted_address" class="mr-3" style="white-space: nowrap; flex-grow: 0;">
                        <label v-translate>Street and number</label><br>
                        {{user.profile_address.place.formatted_address}}
                      </b-col>
                      <b-col v-if="user.profile_address" class="mr-3" style="white-space: nowrap; flex-grow: 0;">
                        <label v-translate>Floor, door, etc.</label><br>
                        {{user.profile_address.address_extra || '-'}}
                      </b-col>
                      <b-col>
                        <br>
                        <b-button size="sm" v-b-modal.modalChangeAddress v-translate>Change Address</b-button>
                      </b-col>
                    </b-form-row>
                    <b-form-row class="mt-3" v-show="!user.profile_address">
                      <b-col>
                        <b-button size="sm" v-b-modal.modalChangeAddress v-translate>Add Address</b-button>
                      </b-col>
                    </b-form-row>

                    <b-modal id="modalChangeAddress" ref="modalChangeAddress" hide-footer>
                      <template #modal-title>
                        <translate>Change User's Address</translate>
                      </template>
                      <template #modal-header-close><img src="@/assets/img/ic_close.svg" alt="×" /></template>
                      <address-create :user="user" is-profile @success="getUser(); $refs.modalChangeAddress.hide();" @cancel="$refs.modalChangeAddress.hide();" />
                    </b-modal>

                  </b-form-group>

                  <b-form-group class="col-12 form-actions">
                    <b-button size="md" :variant="userHasChanges ? 'secondary' : 'dark'" :disabled="!userHasChanges" type="submit">
                      <b-spinner small v-if="updating"></b-spinner>
                      <translate>Save Changes</translate>
                    </b-button>
                  </b-form-group>

                </b-form-row>
              </b-form>
            </b-col>
            <b-col cols="12" md="5" lg="4" xl="3" style="flex-basis: 300px; flex-grow: 0;">
              <b-card class="role-permissions">
                <h5 v-translate>User Roles</h5>
                <b-form-checkbox-group stacked v-model="rolesSelected" :options="appRolesTranslated" :disabled="userHasSystemDisableReason" @change="changeRole" />
              </b-card>
              <b-card class="user-company">
                <h5 v-translate>User Company</h5>
                <div v-if="user.company_id">
                  <company-card :company="user.company" :with-titles="false" vertical profile-picture-size="150px" />
                </div>
                <div class="text-center mt-3" v-else>
                  <b-button size="sm" @click="setCompany">
                    <translate>Add to the Company</translate>
                  </b-button>
                </div>

                <b-modal id="modalSetCompany" ref="modalSetCompany" hide-footer @hide="selectedCompany = null">
                  <template #modal-title>
                    <translate>Add the user to a company</translate>
                  </template>
                  <template #modal-header-close><img src="@/assets/img/ic_close.svg" alt="×" /></template>
                  <form @submit.prevent="setCompany">

                    <b-form-row>
                      <b-form-group class="col-12">
                        <label class="control-label" v-translate>Search the Company</label>
                        <company-autocomplete ref="companyAc" @selected="onSearchCompanySelected" class="w-100" />
                      </b-form-group>
                    </b-form-row>

                    <b-form-row class="mt-3" v-if="selectedCompany">
                      <company-card :company="selectedCompany" />
                    </b-form-row>

                    <b-form-row>
                      <b-form-group class="col-12 form-actions">
                        <b-button type="submit" :disabled="!selectedCompany" variant="secondary" size="md" v-translate>Change</b-button>
                        <b-button type="button" @click.prevent="$refs.modalSetCompany.hide()" variant="outline-secondary" size="md" v-translate>Cancel</b-button>
                      </b-form-group>
                    </b-form-row>

                  </form>
                </b-modal>

              </b-card>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab :title="$gettext('Courier Info')" v-if="userIsCourier">
          <courier-documentation :user="user" with-terms @updated="getUser">
            <courier-vehicles :user="user" @updated="getUser" />
          </courier-documentation>
        </b-tab>
      </b-tabs>
    </b-card>
    <b-modal id="modalChangePassword" ref="modalChangePassword" hide-footer @hide="resetChangePassword">
      <template #modal-title>
        <translate>Force Change User's Password</translate>
      </template>
      <template #modal-header-close><img src="@/assets/img/ic_close.svg" alt="×" /></template>
      <form @submit.prevent="changePassword">
        <b-form-row>
          <div class="form-group col-12">
            <label class="control-label" v-translate>New Password</label>
            <password ref="pwd_check" :min="passwordMinLength" :placeholder="$gettext('New Password')" v-model="newPassword" inputClass="form-control" @input="validatePassword" @score="newPasswordScore = $event" :min-score="passwordMinScore" />
            <b-form-text id="inputLiveHelp" v-translate="{minLength: passwordMinLength}">%{minLength} characters minimum, must contain upper and lower case letters, numbers and symbols</b-form-text>
          </div>
          <div class="form-group col-12">
            <label class="control-label" v-translate>Repeat New Password</label>
            <b-form-input aria-describedby="inputLiveHelp inputLiveFeedback" :placeholder="$gettext('Repeat New Password')" :state="samePassword" type="password" v-model="passwordConfirm" @input="validatePassword" autocomplete="new-password" required></b-form-input>
            <b-form-invalid-feedback id="inputLiveFeedback" v-translate>Passwords don't match</b-form-invalid-feedback>
          </div>
        </b-form-row>
        <b-form-row>
          <div class="text-center form-group col-12 form-actions">
            <b-button type="submit" variant="secondary" :disabled="!samePassword || newPassword.length < passwordMinLength || newPasswordScore < passwordMinScore" size="md" v-translate>Change</b-button>
            <b-button type="button" @click.prevent="$refs.modalChangePassword.hide()" variant="outline-secondary" size="md" v-translate>Cancel</b-button>
          </div>
        </b-form-row>
      </form>
    </b-modal>
    <b-modal id="modalUploadImage" ref="modalUploadImage" hide-footer>
      <template #modal-title>
        <translate>Add a new profile image</translate>
      </template>
      <template #modal-header-close><img src="@/assets/img/ic_close.svg" alt="×" /></template>
      <profile-picture-uploader profilePicture @updateProfileImage="updateProfileImage" />
    </b-modal>

  </b-container>
</template>

<script>
import {
  UserService
} from '@/services/api'
import Cleave from 'vue-cleave'
import CompanyAutocomplete from '@/components/Autocompletes/CompanyAutocomplete'
import CompanyCard from '@/components/CompanyCard'
import ProfilePictureUploader from '@/components/ProfilePictureUploader'
import Password from '@/components/Password'
import AddressCreate from '@/components/AddressCreate'
import VueTagsInput from '@johmun/vue-tags-input'
import ApiList from '@/components/ApiLists/ApiList'
import CourierDocumentation from '@/components/Users/CourierDocumentation'
import CourierVehicles from '@/components/Users/CourierVehicles'
import { hasSystemDisableReason, isPendingApproval } from '@/common/utils/users'

export default {
  name: 'UsersEdit',
  title() {
    return this.$gettextInterpolate(this.$gettext('User #%{userId}'), { userId: this.$options.filters.uuid(this.$route.params.id) })
  },
  components: {
    CompanyAutocomplete,
    Cleave,
    CompanyCard,
    ProfilePictureUploader,
    Password,
    AddressCreate,
    VueTagsInput,
    ApiList,
    CourierDocumentation,
    CourierVehicles
  },
  data() {
    return {
      user: {},
      hash: null,
      userIsGcmAdmin: false,
      userIsCourier: false,
      tabIndex: 0,
      updating: false,
      mobileApps: [],
      userMobileApps: [],
      rolesSelected: [],
      selectedCompany: null,
      preCompany: null,
      phone: null,
      tags: [],
      tag: '',
      autocompleteItems: [],
      userStatuses: [
        'pending',
        'confirmed',
        'suspended'
      ],
      newPassword: '',
      passwordConfirm: '',
      samePassword: null,
      passwordMinLength: 12,
      passwordMinScore: 100,
      newPasswordScore: 0,
      shippingsFields: [
        {
          key: 'id',
          label: this.$gettext('ID')
        },
        {
          key: 'status',
          label: this.$gettext('Status'),
          sortable: true
        },
        {
          key: 'from',
          label: this.$gettext('From'),
          formatter: (value, key, item) => {
            return item.address_from.formatted_address
          }
        },
        {
          key: 'to',
          label: this.$gettext('To'),
          formatter: (value, key, item) => {
            return item.address_to.formatted_address
          }
        },
        {
          key: 'created_at',
          label: this.$gettext('Created At'),
          sortable: true
        },
        {
          key: 'updated_at',
          label: this.$gettext('Updated At'),
          sortable: true
        }
      ],
      setCompanyWarning: false
    }
  },
  computed: {
    bypassCountryCheck() {
      return this.isSuperAdmin ? false : window.countries.find(x => x.iso_code === this.user.country_iso_code).front_config.bypassCountryCheck
    },
    userHasChanges() {
      return JSON.stringify(this.user) !== this.hash
    },
    // Disabled with a reason only Moova can lift (e.g. PendingApproval): the backend rejects enable and disable
    userHasSystemDisableReason() {
      return hasSystemDisableReason(this.user)
    },
    userIsPendingApproval() {
      return isPendingApproval(this.user)
    }
  },
  watch: {
    tag: 'initTagItems'
  },
  mounted() {
    this.getUser()
      .then(() => {
        if (this.$route.query.tab) {
          this.tabIndex = parseInt(this.$route.query.tab)
        }
      })
  },
  methods: {
    async getUser() {
      const params = {
        params: {
          includes: [
            'mobile_apps',
            'tags',
            'vehicles.vehicle_type',
            'disabled_reason'
          ]
        }
      }
      return UserService.get(this.$route.params.id, params)
        .then(response => {
          this.user = response.body
          this.preCompanyId = this.user.company_id
          this.userIsGcmAdmin = this.user.roles.findIndex(x => x.name === this.$roles.GCMR_ADMIN) !== -1 && this.user.roles.findIndex(x => x.name === this.$roles.SUPER_ADMIN) !== -1
          this.userIsCourier = this.user.roles.findIndex(x => x.name === this.$roles.COURIER) !== -1
          this.rolesSelected = [...new Set(this.user.roles.map((role) => {
            return role.id
          }))]
          this.rolesOriginal = [...new Set(this.rolesSelected)]
          this.tags = this.user.tags.map(x => {
            x.text = x.name
            x.classes = 'badge badge-secondary d-inline-flex'
            return x
          })
          if (this.user.active === 0 && this.user.courier_available) {
            this.$http.delete(this.$appConfig.api.baseUrl + 'couriers-available/' + this.user.courier_available.id)
          }
          this.hash = JSON.stringify({ ...this.user })

          this.$http.get(this.$appConfig.api.baseUrl + 'mobile-apps')
            .then(response => {
              this.mobileApps = response.body.data
              this.userMobileApps = this.user.mobile_apps.map(app => {
                return {
                  name: this.mobileApps.find(x => x.id === app.mobile_app_id)?.name || 'NN',
                  version: app.version
                }
              })
              this.updating = false
            })
          if (this.$route.query.setCompany && !this.user.company) {
            this.setCompanyWarning = true
          } else {
            this.setCompanyWarning = false
          }
        })
    },
    updateUser() {
      this.updating = true
      // Only the fields of this form: the backend saves what it receives, and re-sending the whole
      // user would also re-validate the courier documentation (e.g. an expired license answers 422)
      const data = ['name', 'email', 'tin', 'send_notifications', 'second_factor_enabled']
        .reduce((data, key) => ({ ...data, [key]: this.user[key] }), {})
      UserService.update(this.$route.params.id, data)
        .then(response => {
          this.showUpdateSuccess()
          this.getUser()
          this.$refs.modalSetCompany.hide()
        }, () => {
          this.updating = false
          this.showUpdateFailure()
        })
    },
    enableUser() {
      UserService.enable(this.$route.params.id)
        .then(response => {
          this.showUpdateSuccess()
          this.getUser()
        }, () => {
          // The interceptor already shows the backend message; reload so the buttons match the real state
          this.getUser()
        })
    },
    disableUser() {
      UserService.disable(this.$route.params.id)
        .then(response => {
          this.showUpdateSuccess()
          this.getUser()
        }, () => {
          this.getUser()
        })
    },
    changeRole(role) {
      const add = this.rolesSelected.filter(x => !this.rolesOriginal.includes(x))
      const del = this.rolesOriginal.filter(x => !this.rolesSelected.includes(x))
      if (add.length > 0) {
        UserService.addRole(this.$route.params.id, add)
          .then(response => {
            this.makeToast(this.$gettext('Success'), this.$gettext('The role has been selected! 👍'), 'success', false, 4000)
            this.getUser()
          })
      }
      if (del.length > 0) {
        UserService.removeRole(this.$route.params.id, { body: del })
          .then(response => {
            this.makeToast(this.$gettext('Success'), this.$gettext('The role has been set OFF! 👍'), 'success', false, 4000)
            this.getUser()
          })
      }
    },
    validatePassword() {
      this.samePassword = this.newPassword.length > 0 ? this.newPassword === this.passwordConfirm : null
    },
    changePassword() {
      UserService.changePassword({ user_id: this.user.id, password: this.newPassword })
        .then(response => {
          this.showUpdateSuccess()
          this.getUser()
          this.$refs.modalChangePassword.hide()
        })
    },
    resetChangePassword() {
      this.newPassword = ''
      this.passwordConfirm = ''
      this.newPasswordScore = 0
    },
    changePhone() {
      UserService.changePhone({ user_id: this.user.id, phone: this.phone ? this.phone.replace(/[^0-9+]+/g, '') : null })
        .then(response => {
          this.user.phone = this.phone
          this.showUpdateSuccess()
          this.getUser()
          this.$refs.modalChangePhone.hide()
        })
    },
    updateProfileImage(url) {
      this.user.profile_picture = url
      UserService.update(this.$route.params.id, { profile_picture: url })
        .then(response => {
          this.$refs.modalUploadImage.hide()
          this.showUpdateSuccess()
        })
    },
    deleteProfileImage() {
      if (confirm(this.$gettext('Are you sure you want to DELETE the user profile picture?'))) {
        this.updateProfileImage(null)
      }
    },
    onSearchCompanySelected(item) {
      this.selectedCompany = item
    },
    setCompany() {
      if (this.ecosystem.product === 'gcm2') {
        this.selectedCompany = {
          id: this.profile.company.id
        }
      }
      UserService.update(this.$route.params.id, { company_id: this.selectedCompany.id })
        .then(response => {
          this.showUpdateSuccess()
          this.getUser()
          this.$refs.modalSetCompany.hide()
        }, () => {
          this.showUpdateFailure()
        })
    },
    deleteCompany() {
      if (confirm(this.$gettext('Are you sure you want to DELETE user from company?'))) {
        UserService.update(this.$route.params.id, { company_id: null })
          .then(response => {
            this.showUpdateSuccess()
            this.getUser()
            this.$refs.modalSetCompany.hide()
          }, () => {
            this.showUpdateFailure()
          })
      } else {
        return false
      }
    },
    initTagItems() {
      if (this.tag.length < 2) return
      const params = {
        filter_groups: [
          {
            filters: [
              ['name', 'ct', this.tag]
            ]
          }
        ]
      }

      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        this.$http.get(this.$appConfig.api.baseUrl + 'tags', { params })
          .then(response => {
            this.autocompleteItems = response.body.data.map(x => {
              x.text = x.name
              x.classes = 'bg-secondary text-white'
              return x
            })
          }).catch(() => console.warn('Oh. Something went wrong'))
      }, 600)
    },
    checkTag(obj) {
      if (!obj.tag.id) {
        this.$http.post(this.$appConfig.api.baseUrl + 'tags', { name: obj.tag.text.toLowerCase() })
          .then(response => {
            obj.tag.id = response.body.id
            obj.tag.text = response.body.name
            this.$http.post(this.$appConfig.api.baseUrl + 'tags-models', { tag_id: response.body.id, model_type: 'user', model_id: this.user.id + '' })
              .then(response => {
                obj.addTag()
              })
          })
      } else {
        this.$http.post(this.$appConfig.api.baseUrl + 'tags-models', { tag_id: obj.tag.id, model_type: 'user', model_id: this.user.id + '' })
          .then(response => {
            obj.addTag()
          })
      }
    },
    deleteTag(obj) {
      this.$http.delete(this.$appConfig.api.baseUrl + 'tags-models/' + obj.tag.tag_model_id)
        .then(response => {
          obj.deleteTag()
        })
    },
    showUpdateSuccess() {
      this.makeToast(this.$gettext('Success'), this.$gettext('The User has been updated! 👍'), 'success', false, 4000)
    },
    showUpdateFailure() {
      this.makeToast(this.$gettext('ERROR'), this.$gettext('The User couldn\'t be updated! 👎'), 'danger', false, 4000)
    }
  }
}
</script>
<style lang="scss">
.ti-autocomplete {
  ul {
    margin-bottom: 0 !important
  }
}
.ti-tags {
  margin-bottom: 0 !important
}
</style>
